import { supabase } from './supabase';

export interface Article {
  id: string;
  code: string;
  reference?: string;
  name: string;
  description?: string;
  price: number;
  stock?: number;
  active?: boolean;
  created_at: string;
}

export interface ArticleSearchResult {
  id: string;
  code: string;
  reference?: string;
  name: string;
  description?: string;
  price: number;
  stock?: number;
  reserved_stock?: number;
  active?: boolean;
}

class ArticleService {
  /**
   * Recherche d'articles avec auto-complétion
   * @param query - Terme de recherche
   * @param limit - Nombre maximum de résultats (défaut: 3)
   */
  async searchArticles(query: string, limit: number = 3): Promise<ArticleSearchResult[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const searchTerm = query.trim().toLowerCase();

    try {
      const { data, error } = await supabase
        .from('articles')
        .select('id, code, reference, name, description, price, stock, active')
        .or(`code.ilike.%${searchTerm}%,name.ilike.%${searchTerm}%,reference.ilike.%${searchTerm}%`)
        .eq('active', true)
        .limit(limit)
        .order('name', { ascending: true });

      if (error) {
        console.error("Erreur lors de la recherche d'articles:", error);
        return [];
      }

      if (!data || data.length === 0) return [];

      // Calculer le stock réservé (quantités dans commandes non livrées)
      // Utilise une RPC ou une requête par article pour éviter les limites d'URL
      const articleIds = data.map((a: ArticleSearchResult) => a.id);
      const reservedMap = new Map<string, number>();

      try {
        // Requête avec jointure inner : ne retourne que les order_items dont la commande n'est pas livrée/facturée
        const { data: itemsData, error: itemsError } = await supabase
          .from('order_items')
          .select('article_id, quantity, order_id')
          .in('article_id', articleIds);

        if (!itemsError && itemsData && itemsData.length > 0) {
          // Récupérer les statuts des commandes concernées
          const orderIds = [...new Set(itemsData.map((i: { order_id: string }) => i.order_id))];
          const { data: ordersData } = await supabase
            .from('orders')
            .select('id, status')
            .in('id', orderIds);

          // Set des commandes non livrées
          const nonDeliveredOrderIds = new Set(
            (ordersData || [])
              .filter(
                (o: { id: string; status: string }) =>
                  o.status !== 'livre' && o.status !== 'facture'
              )
              .map((o: { id: string }) => o.id)
          );

          for (const item of itemsData as {
            article_id: string;
            quantity: number;
            order_id: string;
          }[]) {
            if (!item.article_id || !nonDeliveredOrderIds.has(item.order_id)) continue;
            const current = reservedMap.get(item.article_id) ?? 0;
            reservedMap.set(item.article_id, current + (item.quantity ?? 0));
          }
        }
      } catch (e) {
        console.error('Erreur calcul stock réservé:', e);
      }

      return data.map((a: ArticleSearchResult) => ({
        ...a,
        reserved_stock: reservedMap.get(a.id) ?? 0,
      }));
    } catch (error) {
      console.error("Erreur lors de la recherche d'articles:", error);
      return [];
    }
  }

  /**
   * Récupère un article par son ID
   */
  async getArticleById(id: string): Promise<Article | null> {
    try {
      const { data, error } = await supabase.from('articles').select('*').eq('id', id).single();

      if (error) {
        console.error("Erreur lors de la récupération de l'article:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'article:", error);
      return null;
    }
  }

  /**
   * Crée un nouvel article
   */
  async createArticle(articleData: Omit<Article, 'id' | 'created_at'>): Promise<Article | null> {
    try {
      const { data, error } = await supabase
        .from('articles')
        .insert([articleData])
        .select()
        .single();

      if (error) {
        console.error("Erreur lors de la création de l'article:", error);
        return null;
      }

      return data;
    } catch (error) {
      console.error("Erreur lors de la création de l'article:", error);
      return null;
    }
  }
}

export const articleService = new ArticleService();
